'use client';

import { Canvas, useFrame } from '@react-three/fiber';
import { Icosahedron, Sphere, MeshDistortMaterial } from '@react-three/drei';
import { useRef, useMemo, Suspense } from 'react';
import * as THREE from 'three';

const Satellites = () => {
    const sat1 = useRef<any>(null);
    const sat2 = useRef<any>(null);
    const sat3 = useRef<any>(null);

    useFrame((state) => {
        const time = state.clock.getElapsedTime();

        // Sat 1: Horizontal Wide Orbit (Blue - Brand)
        if (sat1.current) {
            sat1.current.position.x = Math.cos(time * 0.4) * 4.0;
            sat1.current.position.z = Math.sin(time * 0.4) * 4.0;
            sat1.current.position.y = Math.sin(time * 0.8) * 0.5; // Slight bobbing
        }

        // Sat 2: Tilted Elliptical Orbit (Green - Growth)
        if (sat2.current) {
            const t = time * 0.3 + 2; // Offset time
            const r = 4.8;
            sat2.current.position.x = Math.cos(t) * r;
            sat2.current.position.y = Math.sin(t) * r * 0.6; // Tilt
            sat2.current.position.z = Math.sin(t) * r;
        }

        // Sat 3: Fast Inner Orbit (Purple - Strategy)
        if (sat3.current) {
            const t = time * 0.6 + 4;
            const r = 3.2;
            sat3.current.position.x = Math.sin(t) * r;
            sat3.current.position.y = Math.cos(t) * r;
            sat3.current.position.z = Math.sin(t * 0.5) * 1.5;
        }
    });

    return (
        <group>
            {/* Satellite 1: Brand Blue */}
            <mesh ref={sat1}>
                <sphereGeometry args={[0.08, 16, 16]} />
                <meshBasicMaterial color="#4f46e5" />
            </mesh>

            {/* Satellite 2: Growth Green */}
            <mesh ref={sat2}>
                <sphereGeometry args={[0.06, 16, 16]} />
                <meshBasicMaterial color="#22c55e" />
            </mesh>

            {/* Satellite 3: Strategy Purple */}
            <mesh ref={sat3}>
                <sphereGeometry args={[0.07, 16, 16]} />
                <meshBasicMaterial color="#a855f7" />
            </mesh>
        </group>
    );
};

const PolishedGlobe = () => {
    const mainRef = useRef<any>(null);
    const glowRef = useRef<any>(null);
    const streamRef = useRef<any>(null);
    const dotsRef = useRef<any>(null);

    // Stream Data (Upward Spiral) - Polish: Smoother flow
    const streamData = useMemo(() => {
        const count = 150; // Reduced quantity as requested
        const pos = new Float32Array(count * 3);
        const speeds = new Float32Array(count);
        const sizes = new Float32Array(count);

        for (let i = 0; i < count; i++) {
            const angle = Math.random() * Math.PI * 2;
            const radius = 1.0 + Math.random() * 2.5; // Slightly wider spread
            pos[i * 3] = Math.cos(angle) * radius;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 10; // Taller column
            pos[i * 3 + 2] = Math.sin(angle) * radius;
            speeds[i] = 0.005 + Math.random() * 0.015; // Much slower, gentle float
            sizes[i] = Math.random();
        }

        return { pos, speeds, sizes };
    }, []);

    // Texture for Round Dots
    const circleTexture = useMemo(() => {
        if (typeof window === 'undefined') return null;
        const canvas = document.createElement('canvas');
        canvas.width = 32;
        canvas.height = 32;
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.beginPath();
            ctx.arc(16, 16, 14, 0, 2 * Math.PI);
            ctx.fillStyle = 'white';
            ctx.fill();
        }
        return new THREE.CanvasTexture(canvas);
    }, []);

    useFrame((state) => {
        const time = state.clock.getElapsedTime();

        // 1. Elegant Rotation
        if (mainRef.current) {
            mainRef.current.rotation.y = time * 0.1;
            mainRef.current.rotation.z = Math.sin(time * 0.15) * 0.1; // Gentle tilt
        }
        if (dotsRef.current) {
            dotsRef.current.rotation.y = time * 0.1;
            dotsRef.current.rotation.z = Math.sin(time * 0.15) * 0.1;
        }
        if (glowRef.current) {
            // Counter rotation for complexity
            glowRef.current.rotation.y = time * 0.05;
        }

        // 2. Stream Animation
        if (streamRef.current) {
            const positions = streamRef.current.geometry.attributes.position.array as Float32Array;
            for (let i = 0; i < 150; i++) {
                positions[i * 3 + 1] += streamData.speeds[i];
                if (positions[i * 3 + 1] > 5) {
                    positions[i * 3 + 1] = -5;
                }
            }
            streamRef.current.geometry.attributes.position.needsUpdate = true;
            // streamRef.current.rotation.y = -time * 0.05; // Spiral flow
        }
    });

    return (
        <group rotation={[0.1, 0, 0.2]}>

            {/* 1. Main High-Def Wireframe (Outer Shell) */}
            <Icosahedron ref={mainRef} args={[2.8, 2]}>
                <meshStandardMaterial
                    color="#4f46e5" // Indigo-600
                    wireframe={true}
                    transparent={true}
                    opacity={0.3} // Increased visibility
                    emissive="#4f46e5"
                    emissiveIntensity={0.5}
                />
            </Icosahedron>

            {/* 2. Inner "Energy" Core (Polished Glow, No Mesh Lines) */}
            <Sphere ref={glowRef} args={[2.4, 32, 32]}>
                <meshBasicMaterial
                    color="#818cf8" // Indigo-400
                    transparent={true}
                    opacity={0.05}
                    blending={THREE.AdditiveBlending}
                />
            </Sphere>

            {/* 3. Refined Data Stream (Subtle Flow vs Chaotic Dots) */}
            <points ref={streamRef}>
                <bufferGeometry>
                    <bufferAttribute
                        attach="attributes-position"
                        args={[streamData.pos, 3]}
                    />
                    <bufferAttribute
                        attach="attributes-size"
                        args={[streamData.sizes, 1]}
                    />
                </bufferGeometry>
                <pointsMaterial
                    size={0.12} // Increased slightly for visibility with alphaMap
                    color="#4f46e5" // Theme Blue (Indigo-600)
                    transparent={true}
                    opacity={0.8}
                    sizeAttenuation={true}
                    map={circleTexture}
                    alphaMap={circleTexture}
                    alphaTest={0.5}
                    depthWrite={false}
                />
            </points>

            {/* 4. Vertex Dots (Intersections) */}
            <points ref={dotsRef}>
                <icosahedronGeometry args={[2.8, 2]} />
                <pointsMaterial
                    size={0.12}
                    color="#4f46e5" // Indigo-600
                    transparent={true}
                    opacity={1}
                    sizeAttenuation={true}
                    map={circleTexture}
                    alphaMap={circleTexture}
                    alphaTest={0.5}
                    depthWrite={false}
                />
            </points>

        </group>
    );
};

export default function MobileBrandLabScene() {
    return (
        <div className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
            {/* Adjusted camera Z position for mobile visibility (zoomed out) */}
            <Canvas
                camera={{ position: [0, 1, 19], fov: 50 }}
                gl={{ antialias: true, alpha: true, powerPreference: "high-performance" }}
                dpr={[1, 2]}
            >
                {/* Enhanced Lighting for "Polish" */}
                <ambientLight intensity={0.5} />
                <pointLight position={[10, 10, 10]} intensity={1.5} color="#818cf8" />
                <pointLight position={[-10, -5, -5]} intensity={0.5} color="#c084fc" />

                <Suspense fallback={null}>
                    <PolishedGlobe />
                    <Satellites />
                </Suspense>
            </Canvas>
        </div>
    );
}
